import React from 'react';
import { cn } from '../../utils/cn';

/**
 * Button - Reusable button component with variants
 *
 * @param {object} props - Component props
 * @param {React.ReactNode} props.children - Button content
 * @param {'primary'|'secondary'|'ghost'} [props.variant='primary'] - Button style variant
 * @param {'sm'|'md'|'lg'} [props.size='md'] - Button size
 * @param {string} [props.className=''] - Additional CSS classes
 * @param {Function} [props.onClick] - Click handler
 * @param {boolean} [props.disabled=false] - Disabled state
 * @param {string} [props.type='button'] - Button type
 * @param {string} [props.href] - If provided, renders as link
 */
const Button = ({
  children,
  variant = 'primary',
  size = 'md',
  className = '',
  onClick,
  disabled = false,
  type = 'button',
  href,
  ...rest
}) => {
  // Size classes
  const sizeClasses = {
    sm: 'px-4 py-2 text-sm',
    md: 'px-6 py-3 text-base',
    lg: 'px-8 py-4 text-lg'
  };

  // Variant styles
  const variantStyles = {
    primary: {
      className: 'font-medium transition-all duration-300 hover:scale-105',
      style: {
        background: 'var(--gradient-primary)',
        color: 'var(--color-bg-primary)',
        boxShadow: disabled ? 'none' : '0 0 20px rgba(0, 212, 170, 0)'
      },
      hoverStyle: !disabled && 'hover:shadow-lg hover:shadow-[var(--color-accent)]/20'
    },
    secondary: {
      className: 'font-medium border-2 transition-all duration-300',
      style: {
        borderColor: 'var(--color-border)',
        color: 'var(--color-text-primary)',
        background: 'transparent'
      },
      hoverStyle: !disabled && 'hover:border-[var(--color-accent)]/50 hover:bg-[var(--color-accent)]/10'
    },
    ghost: {
      className: 'font-medium transition-all duration-300',
      style: {
        color: 'var(--color-text-muted)',
        background: 'transparent'
      },
      hoverStyle: !disabled && 'hover:text-[var(--color-accent)] hover:bg-white/5'
    }
  };

  const currentVariant = variantStyles[variant] || variantStyles.primary;

  const baseClasses = cn(
    'rounded-xl font-semibold transition-all duration-300',
    'focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)] focus:ring-offset-2',
    'focus:ring-offset-[var(--color-bg-primary)]',
    sizeClasses[size],
    currentVariant.className,
    currentVariant.hoverStyle,
    disabled && 'opacity-50 cursor-not-allowed',
    className
  );

  // Render as link if href is provided
  if (href) {
    return (
      <a
        href={href}
        className={baseClasses}
        style={currentVariant.style}
        {...rest}
      >
        {children}
      </a>
    );
  }

  // Render as button
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={baseClasses}
      style={currentVariant.style}
      {...rest}
    >
      {children}
    </button>
  );
};

export default Button;
